import { useMemo, useCallback } from 'react';
import { resolveClientPersona, PERSONA_CONFIGS, PersonaType, PersonaAssistantConfig } from '../config/personaConfig';
import { ClickableSuggestion } from '../components/types';

interface UsePersonaParams {
  permissions: string[];
  companyId: number | null | undefined;
  partnerId?: number | null;
  userName: string;
}

interface UsePersonaReturn {
  persona: PersonaType;
  personaConfig: PersonaAssistantConfig;
  generateWelcomeMessage: () => string;
  generateWelcomeMessageForNewChat: () => string;
  generateSuggestions: () => ClickableSuggestion[];
}

export function usePersona({ permissions, companyId, partnerId, userName }: UsePersonaParams): UsePersonaReturn {
  const persona = useMemo(
    () => resolveClientPersona(permissions, companyId, partnerId),
    [permissions, companyId, partnerId]
  );

  const personaConfig = PERSONA_CONFIGS[persona];

  const generateWelcomeMessage = useCallback(
    () => personaConfig.welcomeMessage(userName),
    [personaConfig, userName]
  );

  const generateWelcomeMessageForNewChat = useCallback(
    () => personaConfig.welcomeMessage(userName),
    [personaConfig, userName]
  );

  const generateSuggestions = useCallback((): ClickableSuggestion[] => {
    return personaConfig.suggestions.slice(0, 6).map((s, i) => ({
      id: `persona-${i}`,
      label: s.label,
      description: '',
      action: 'send_message' as const,
      value: s.value,
      icon: 'smart_toy',
      category: 'action' as const,
    }));
  }, [personaConfig]);

  return {
    persona,
    personaConfig,
    generateWelcomeMessage,
    generateWelcomeMessageForNewChat,
    generateSuggestions,
  };
}
